export const queryKeys = {
  // Auth
  auth: {
    me: ['auth', 'me'] as const,
    verifyInvite: (token: string) => ['auth', 'verify-invite', token] as const,
  },

  // Projects
  projects: {
    all: ['projects'] as const,
    list: (filters?: Record<string, unknown>) => ['projects', 'list', filters] as const,
    detail: (id: string) => ['projects', 'detail', id] as const,
  },

  // Tasks
  tasks: {
    all: ['tasks'] as const,
    list: (filters?: Record<string, unknown>) => ['tasks', 'list', filters] as const,
    detail: (id: string) => ['tasks', 'detail', id] as const,
    byProject: (projectId: string) => ['tasks', 'project', projectId] as const,
    comments: (taskId: string) => ['tasks', 'comments', taskId] as const,
    activities: (taskId: string) => ['tasks', 'activities', taskId] as const,
    attachments: (taskId: string) => ['tasks', 'attachments', taskId] as const,
    searchAssigned: (params: {
      search: string;
      includeCompleted?: boolean;
      page?: number;
      pageSize?: number;
    }) =>
      [
        'tasks',
        'search-assigned',
        params.search,
        params.includeCompleted,
        params.page,
        params.pageSize,
      ] as const,
  },

  // Project-scoped custom masters
  projectMasters: {
    taskStatuses: (projectId: string) => ['projectMasters', 'taskStatuses', projectId] as const,
    taskTypes: (projectId: string) => ['projectMasters', 'taskTypes', projectId] as const,
  },

  // Org-level task masters (settings)
  orgMasters: {
    taskStatuses: ['orgMasters', 'taskStatuses'] as const,
    taskTypes: ['orgMasters', 'taskTypes'] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    list: (filters?: Record<string, unknown>) => ['users', 'list', filters] as const,
    detail: (id: string) => ['users', 'detail', id] as const,
    orgList: ['orgUsers'] as const,
  },

  // Teams
  teams: {
    all: ['teams'] as const,
    detail: (id: string) => ['teams', 'detail', id] as const,
  },

  // Clients
  clients: {
    all: ['clients'] as const,
    detail: (id: string) => ['clients', 'detail', id] as const,
  },

  // Project team (React Query, multi-project support)
  projectTeam: {
    members: (projectId: string) => ['projectTeam', 'members', projectId] as const,
    permissions: (projectId: string) => ['projectTeam', 'permissions', projectId] as const,
  },

  // Project modules (React Query, multi-project support)
  projectModules: {
    withFeatures: (projectId: string) => ['projectModules', 'withFeatures', projectId] as const,
  },

  // Time entries
  time: {
    all: ['time'] as const,
    activeEntry: ['time', 'activeEntry'] as const,
    byTask: (taskId: string) => ['time', 'task', taskId] as const,
  },

  // Timesheets
  timesheets: {
    daily: (date: string) => ['timesheets', 'daily', date] as const,
    comments: (timesheetId: string) => ['timesheets', 'comments', timesheetId] as const,
    activity: (timesheetId: string) => ['timesheets', 'activity', timesheetId] as const,
    pmView: (params: Record<string, unknown>) => ['timesheets', 'pm-view', params] as const,
  },

  // Tags (settings, super admin – timesheet tag conditions)
  tags: {
    all: ['tags'] as const,
    list: (params?: { active?: boolean }) => ['tags', 'list', params] as const,
    detail: (id: string) => ['tags', 'detail', id] as const,
    conditionMasters: ['tags', 'conditionMasters'] as const,
    conditions: (tagId: string) => ['tags', 'conditions', tagId] as const,
  },

  // Notifications
  notifications: {
    all: ['notifications'] as const,
    unread: ['notifications', 'unread'] as const,
    config: ['notifications', 'config'] as const,
  },

  // My Work per-view section tasks (parallel fetches on the My Work page)
  myWork: {
    // Use allSectionTasks as the invalidation prefix — it matches every per-view
    // key regardless of viewKey / search / filter params.
    allSectionTasks: ['myWork', 'sectionTasks'] as const,
    sectionTasks: (viewKey: string) => ['myWork', 'sectionTasks', viewKey] as const,
  },

  // My Work views (flat list — no scope split)
  myWorkViews: {
    list: ['myWorkViews', 'list'] as const,
    summary: (projectId?: string) => ['myWorkViews', 'summary', projectId ?? 'org'] as const,
    filterSchema: ['myWorkViews', 'filter-schema'] as const,
  },

  // Org users (used as mentionable team members fallback)
  orgUsers: {
    asTeamMembers: ['orgUsers', 'asTeamMembers'] as const,
  },

  // Designations (settings)
  designations: {
    all: ['designations'] as const,
    list: (params?: object) => ['designations', 'list', params] as const,
    detail: (id: number) => ['designations', 'detail', id] as const,
  },

  // Organisations (settings, super admin)
  organisations: {
    all: ['organisations'] as const,
    list: () => ['organisations', 'list'] as const,
  },

  // Master
  master: {
    all: ['master'] as const,
    byParentCode: (code: string, params?: Record<string, unknown>) =>
      ['master', 'by-parent-code', code, params] as const,
  },

  // Features
  features: {
    all: ['features'] as const,
    detail: (id: string) => ['features', 'detail', id] as const,
    comments: (featureId: string) => ['features', 'comments', featureId] as const,
    activities: (featureId: string) => ['features', 'activities', featureId] as const,
    attachments: (featureId: string) => ['features', 'attachments', featureId] as const,
  },

  // Modules
  modules: {
    all: ['modules'] as const,
    detail: (id: string) => ['modules', 'detail', id] as const,
    comments: (moduleId: string) => ['modules', 'comments', moduleId] as const,
    activities: (moduleId: string) => ['modules', 'activities', moduleId] as const,
    attachments: (moduleId: string) => ['modules', 'attachments', moduleId] as const,
  },

  // User View feature
  taskViewOptions: ['taskViewOptions'] as const,
  projectViews: (projectId: string) => ['projectViews', projectId] as const,
  taskCustomFields: (projectId: string, includeArchived = false) =>
    ['taskCustomFields', projectId, includeArchived] as const,
  projectView: (projectId: string, viewId: string) =>
    ['projectViews', projectId, 'view', viewId] as const,

  // Project setup templates (statuses + custom fields)
  projectSetupTemplates: {
    all: ['projectSetupTemplates'] as const,
    list: ['projectSetupTemplates', 'list'] as const,
    detail: (id: number) => ['projectSetupTemplates', 'detail', id] as const,
  },

  // Workspace-level project limits (custom fields + templates)
  projectLimits: ['projectLimits'] as const,

  // BRD / Clara Conversations
  conversations: {
    list: (params?: Record<string, unknown>) => ['conversations', 'list', params] as const,
    detail: (conversationId: string) => ['conversations', 'detail', conversationId] as const,
    messages: (
      conversationId: string,
      params?: { sort?: string; page?: number; page_size?: number },
    ) => ['conversations', conversationId, 'messages', params] as const,
  },

  // Monitoring (Sentry proxy)
  monitoring: {
    errors: (query: string, sort: string, project?: string) =>
      ['monitoring', 'errors', query, sort, project] as const,
    errorDetail: (issueId: string) => ['monitoring', 'errors', 'detail', issueId] as const,
    stats: (project?: string) => ['monitoring', 'stats', project] as const,
  },

  // Meetings
  meetings: {
    all: ['meetings'] as const,
    byProject: (projectId: string, params?: Record<string, unknown>) =>
      ['meetings', 'project', projectId, params] as const,
    detail: (projectId: string, meetingId: string) =>
      ['meetings', 'project', projectId, 'detail', meetingId] as const,
    byDate: (date: string) => ['meetings', 'bydate', date] as const,
    calendar: (params: {
      startDate: string;
      endDate: string;
      projectId?: number;
      includeCancelled?: boolean;
    }) => ['meetings', 'calendar', params] as const,
    myRemainingHourRequests: ['meetings', 'my-remaining-hour-requests'] as const,
    note: (projectId: string | null, meetingId: string) =>
      ['meetings', 'note', projectId, meetingId] as const,
    analytics: {
      org: (params?: { from_date?: string; to_date?: string }) =>
        ['meetings', 'analytics', 'org', params] as const,
      project: (projectId: string, params?: { from_date?: string; to_date?: string }) =>
        ['meetings', 'analytics', 'project', projectId, params] as const,
      my: (params?: { from_date?: string; to_date?: string }) =>
        ['meetings', 'analytics', 'my', params] as const,
    },
    clara: {
      summary: (meetingId: string) => ['meetings', 'clara', 'summary', meetingId] as const,
      transcript: (meetingId: string, page: number, pageSize: number) =>
        ['meetings', 'clara', 'transcript', meetingId, page, pageSize] as const,
      transcriptInfinite: (meetingId: string, pageSize: number) =>
        ['meetings', 'clara', 'transcript', meetingId, 'infinite', pageSize] as const,
      recording: (meetingId: string) => ['meetings', 'clara', 'recording', meetingId] as const,
      mom: (meetingId: string) => ['meetings', 'clara', 'mom', meetingId] as const,
    },
  },

  // RBAC
  rbac: {
    templates: {
      all: ['rbac', 'templates'] as const,
      detail: (id: string) => ['rbac', 'templates', 'detail', id] as const,
    },
    allUserPermissions: (params?: { search?: string; template_ids?: string }) =>
      ['rbac', 'all-user-permissions', params] as const,
    userPermission: (userId: string) => ['rbac', 'user-permission', userId] as const,
    permissionMatrix: (userId: string, projectId?: string) =>
      ['rbac', 'permission-matrix', userId, projectId] as const,
    projectPermissions: (projectId: string) => ['rbac', 'project-permissions', projectId] as const,
    elevations: (filters?: Record<string, unknown>) => ['rbac', 'elevations', filters] as const,
    auditLogs: (filters?: Record<string, unknown>) => ['rbac', 'audit-logs', filters] as const,
    myPermissions: (projectId?: string) => ['rbac', 'my-permissions', projectId] as const,
  },

  // Admin Overview Config
  overviewAdmin: {
    widgetDefs: ['overviewAdmin', 'widgetDefs'] as const,
    chartTypes: ['overviewAdmin', 'chartTypes'] as const,
    defaultTemplate: (templateCode: string) =>
      ['overviewAdmin', 'defaultTemplate', templateCode] as const,
  },

  // Project Overview
  projectOverview: {
    layout: (projectId: string) => ['projectOverview', 'layout', projectId] as const,
    widgetDefs: (projectId: string) => ['projectOverview', 'widgetDefs', projectId] as const,
    kpis: (projectId: string, dateRange?: Record<string, string>) =>
      ['projectOverview', 'kpis', projectId, dateRange] as const,
    widgetData: (projectId: string, widgetKey: string, params?: Record<string, unknown>) =>
      ['projectOverview', 'widgetData', projectId, widgetKey, params] as const,
  },

  // Report Views (saved presets)
  reportViews: {
    list: (reportType: string) => ['reportViews', 'list', reportType] as const,
    detail: (viewId: string) => ['reportViews', 'detail', viewId] as const,
  },

  // Timesheet Reports
  timesheetReports: {
    report: (params: Record<string, unknown>) => ['timesheetReports', 'report', params] as const,
    summary: (params: Record<string, unknown>) => ['timesheetReports', 'summary', params] as const,
    detail: (id: string, params: Record<string, unknown>) =>
      ['timesheetReports', 'detail', id, params] as const,
  },

  // LLM Usage (admin — product-wide LLM API cost tracking)
  llmUsage: {
    all: ['llmUsage'] as const,
    report: (filters?: Record<string, unknown>) => ['llmUsage', 'report', filters] as const,
  },
} as const;
